/**
 * CreepDetailDrawer — L2 drill-down for Cognara Cost-Creep and Schedule-Creep
 * alert rows on the Portfolio dashboard. One drawer, two payload shapes.
 */
import { useEffect, useState } from 'react';
import { X, Loader2, AlertTriangle, DollarSign, CalendarClock, ArrowRight, Wrench } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const Stat = ({ label, value, tone, T, testId }) => (
  <div className="rounded-lg px-3 py-2" style={{ background: T.appBg, border: `1px solid ${T.border}` }} data-testid={testId}>
    <div className="text-[9px] uppercase tracking-wider" style={{ color: T.slate }}>{label}</div>
    <div className="text-sm font-mono font-semibold" style={{ color: tone || T.text }}>{value}</div>
  </div>
);

const Bar = ({ label, value, max, T }) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between text-[10px]">
      <span className="truncate pr-2" style={{ color: T.text2 }}>{label}</span>
      <span className="font-mono" style={{ color: T.text }}>{money(value)}</span>
    </div>
    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: T.border }}>
      <div className="h-full rounded-full transition-all duration-500"
           style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, background: T.primary }} />
    </div>
  </div>
);

const Section = ({ title, icon: Icon, children, T, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    <div className="flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5" style={{ color: T.primary }} />}
      <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: T.slate }}>{title}</span>
    </div>
    {children}
  </div>
);

export const CreepDetailDrawer = ({ kind, target, T, onClose, onNavigate }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!target) return;
    let alive = true;
    setLoading(true); setError(null); setData(null);
    const url = kind === 'cost'
      ? `/v1/projects/${target.projectId}/cc/cbs/creep-detail/${encodeURIComponent(target.costCode)}`
      : `/portfolios/portfolio_001/schedule-creep/detail?project_id=${encodeURIComponent(target.projectId)}&milestone_id=${encodeURIComponent(target.milestoneId)}`;
    api.get(url)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load detail'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [kind, target]);

  if (!target) return null;
  const f = data?.financials || {};
  const spend = data?.spend || {};
  const maxVendor = Math.max(1, ...(spend.byVendor || []).map((v) => v.amount));
  const sevTone = (s) => (s === 'CRITICAL' ? T.crimson : s === 'HIGH' ? T.crimson : T.amber);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="creep-detail-drawer">
      <div className="absolute inset-0" style={{ background: 'rgba(3,7,18,0.55)', backdropFilter: 'blur(3px)' }}
           onClick={onClose} data-testid="creep-drawer-backdrop" />
      <aside className="relative h-full w-full max-w-[560px] overflow-y-auto shadow-2xl"
             style={{ background: T.card, borderLeft: `1px solid ${T.border}` }}>
        <header className="sticky top-0 z-10 px-4 py-3 flex items-start gap-3"
                style={{ background: T.card, borderBottom: `1px solid ${T.border}` }}>
          {kind === 'cost'
            ? <DollarSign className="w-4 h-4 mt-0.5" style={{ color: T.crimson }} />
            : <CalendarClock className="w-4 h-4 mt-0.5" style={{ color: T.amber }} />}
          <div className="flex-1 min-w-0">
            <div className="text-[9px] uppercase tracking-wider" style={{ color: T.slate }}>
              {kind === 'cost' ? 'Cost Creep · L2 Detail' : 'Schedule Creep · L2 Detail'}
            </div>
            <div className="text-sm font-semibold truncate" style={{ color: T.text }} data-testid="creep-drawer-title">
              {kind === 'cost'
                ? (data?.costCodeName || target.costCodeName || target.costCode)
                : (data?.milestoneName || target.milestoneName)}
            </div>
            <div className="text-[10px] font-mono truncate" style={{ color: T.slate }}>
              {kind === 'cost'
                ? `${target.projectId} · ${target.costCode}${data?.costCategory ? ` · ${data.costCategory}` : ''}`
                : `${data?.projectName || target.projectName || target.projectId} · ${data?.phase || ''}`}
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:opacity-70" data-testid="creep-drawer-close">
            <X className="w-4 h-4" style={{ color: T.slate }} />
          </button>
        </header>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-16 text-xs" style={{ color: T.slate }}
               data-testid="creep-drawer-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading L2 detail…
          </div>
        )}
        {error && !loading && (
          <div className="px-4 py-10 text-center text-xs" style={{ color: T.crimson }} data-testid="creep-drawer-error">{error}</div>
        )}

        {data && !loading && (
          <div className="p-4 space-y-5">
            {kind === 'cost' ? (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Budget"   value={money(f.currentBudget)} T={T} testId="creep-stat-budget" />
                  <Stat label="Actual"   value={money(f.actual)} tone={f.overrun > 0 ? T.crimson : T.text} T={T} testId="creep-stat-actual" />
                  <Stat label="Committed" value={money(f.committed)} T={T} testId="creep-stat-committed" />
                  <Stat label="EAC"      value={money(f.eac)} T={T} testId="creep-stat-eac" />
                  <Stat label="VAC"      value={money(f.vac)} tone={(f.vac || 0) < 0 ? T.crimson : T.emerald} T={T} testId="creep-stat-vac" />
                  <Stat label="CPI"      value={f.cpi ?? '—'} tone={(f.cpi || 1) < 1 ? T.crimson : T.emerald} T={T} testId="creep-stat-cpi" />
                </div>
                <div className="rounded-lg px-3 py-2 text-[11px]" style={{ background: `${T.crimson}12`, border: `1px solid ${T.crimson}33` }}>
                  <span style={{ color: T.text }}>
                    {f.utilisationPct}% of budget consumed at {f.physicalProgressPct}% physical progress
                  </span>
                  {f.overrun > 0 && (
                    <span className="font-mono ml-1" style={{ color: T.crimson }}>· overrun {money(f.overrun)}</span>
                  )}
                </div>

                <Section title="Cognara diagnosis" icon={AlertTriangle} T={T} testId="creep-drawer-diagnosis">
                  <ul className="space-y-1.5">
                    {(data.diagnosis?.drivers || []).map((d, i) => (
                      <li key={i} className="text-[11px] leading-relaxed pl-3 border-l-2"
                          style={{ color: T.text2, borderColor: T.crimson }}>{d}</li>
                    ))}
                  </ul>
                </Section>

                <Section title="Recommended actions" icon={Wrench} T={T} testId="creep-drawer-actions">
                  <ul className="space-y-1.5">
                    {(data.diagnosis?.actions || []).map((a, i) => (
                      <li key={i} className="text-[11px] leading-relaxed pl-3 border-l-2"
                          style={{ color: T.text2, borderColor: T.emerald }}>{a}</li>
                    ))}
                  </ul>
                </Section>

                {(spend.byVendor || []).length > 0 && (
                  <Section title={`Spend by vendor · ${spend.transactionCount} txns`} T={T} testId="creep-drawer-vendors">
                    <div className="space-y-2">
                      {spend.byVendor.map((v) => <Bar key={v.name} label={v.name} value={v.amount} max={maxVendor} T={T} />)}
                    </div>
                  </Section>
                )}

                {(spend.recentInvoices || []).length > 0 && (
                  <Section title="Recent invoices" T={T} testId="creep-drawer-invoices">
                    <div className="space-y-1">
                      {spend.recentInvoices.map((iv, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="font-mono" style={{ color: T.slate, minWidth: 66 }}>{iv.date}</span>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{iv.vendor || iv.invoiceNumber}</span>
                          <span className="font-mono" style={{ color: T.text }}>{money(iv.certified || iv.gross)}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {(data.linkedActivities || []).length > 0 && (
                  <Section title="Linked schedule activities" T={T} testId="creep-drawer-activities">
                    <div className="space-y-1">
                      {data.linkedActivities.slice(0, 8).map((t, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{t.name}</span>
                          {t.isCritical && (
                            <span className="px-1.5 rounded-full" style={{ background: `${T.crimson}20`, color: T.crimson }}>CP</span>
                          )}
                          <span className="font-mono" style={{ color: T.text }}>{t.percentComplete ?? 0}%</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {(data.siblings || []).length > 0 && (
                  <Section title="Peers under the same parent" T={T} testId="creep-drawer-siblings">
                    <div className="space-y-1">
                      {data.siblings.map((s) => (
                        <div key={s.costCode} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="font-mono" style={{ color: T.slate, minWidth: 70 }}>{s.costCode}</span>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{s.costCodeName}</span>
                          <span className="font-mono" style={{ color: (s.utilisationPct || 0) > 100 ? T.crimson : T.text }}>
                            {s.utilisationPct ?? '—'}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                <button onClick={() => onNavigate?.(`/cbs-matrix?project=${target.projectId}&code=${target.costCode}`)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold"
                        style={{ background: T.primary, color: '#fff' }}
                        data-testid="creep-drawer-open-cbs">
                  Open in CBS Matrix <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="Baseline" value={data.baselineDate || '—'} T={T} testId="creep-stat-baseline" />
                  <Stat label="Forecast" value={data.forecastDate || '—'} tone={T.amber} T={T} testId="creep-stat-forecast" />
                  <Stat label="Slip"     value={`+${data.slipDays ?? 0}d`} tone={sevTone(data.severity)} T={T} testId="creep-stat-slip" />
                  <Stat label="Complete" value={`${data.percentComplete ?? 0}%`} T={T} testId="creep-stat-progress" />
                  <Stat label="Float"    value={data.totalFloat ?? '—'} tone={Number(data.totalFloat || 0) <= 0 ? T.crimson : T.text} T={T} testId="creep-stat-float" />
                  <Stat label="Project SPI" value={data.projectKpis?.spi ?? '—'}
                        tone={(data.projectKpis?.spi || 1) < 1 ? T.crimson : T.emerald} T={T} testId="creep-stat-spi" />
                </div>

                <Section title="Cognara diagnosis" icon={AlertTriangle} T={T} testId="creep-drawer-diagnosis">
                  <ul className="space-y-1.5">
                    {(data.diagnosis?.drivers || []).map((d, i) => (
                      <li key={i} className="text-[11px] leading-relaxed pl-3 border-l-2"
                          style={{ color: T.text2, borderColor: T.amber }}>{d}</li>
                    ))}
                  </ul>
                </Section>

                <Section title="Recovery levers" icon={Wrench} T={T} testId="creep-drawer-actions">
                  <ul className="space-y-1.5">
                    {(data.diagnosis?.actions || []).map((a, i) => (
                      <li key={i} className="text-[11px] leading-relaxed pl-3 border-l-2"
                          style={{ color: T.text2, borderColor: T.emerald }}>{a}</li>
                    ))}
                  </ul>
                </Section>

                {(data.predecessors || []).length > 0 && (
                  <Section title="Driving predecessors" T={T} testId="creep-drawer-predecessors">
                    <div className="space-y-1">
                      {data.predecessors.map((p, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{p.name}</span>
                          <span className="font-mono" style={{ color: T.slate }}>{p.percentComplete ?? 0}%</span>
                          <span className="font-mono font-semibold" style={{ color: (p.slipDays || 0) > 0 ? T.crimson : T.emerald }}>
                            {(p.slipDays || 0) > 0 ? `+${p.slipDays}d` : 'on time'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {(data.successors || []).length > 0 && (
                  <Section title="Downstream exposure" T={T} testId="creep-drawer-successors">
                    <div className="space-y-1">
                      {data.successors.map((s, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{s.name}</span>
                          <span className="font-mono" style={{ color: T.slate }}>{s.start}</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {(data.otherSlippingMilestones || []).length > 0 && (
                  <Section title="Other milestones slipping on this project" T={T} testId="creep-drawer-other-milestones">
                    <div className="space-y-1">
                      {data.otherSlippingMilestones.map((m, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b" style={{ borderColor: T.border }}>
                          <span className="flex-1 truncate" style={{ color: T.text2 }}>{m.name}</span>
                          <span className="font-mono" style={{ color: T.slate }}>{m.forecastDate}</span>
                          <span className="font-mono font-semibold" style={{ color: T.crimson }}>+{m.slipDays}d</span>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                <button onClick={() => onNavigate?.(`/scheduling?project=${data.projectId}`)}
                        className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] font-semibold"
                        style={{ background: T.primary, color: '#fff' }}
                        data-testid="creep-drawer-open-schedule">
                  Open Macro Schedule <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        )}
      </aside>
    </div>
  );
};

export default CreepDetailDrawer;
